/**
 * Per-viewer UI preferences persisted in localStorage (HS2-9G5PQ9).
 *
 * These are conveniences (which shell rails are collapsed), not authoritative
 * state: every access is wrapped so a private window, blocked/cleared site data,
 * or a missing `localStorage` (e.g. the unit-test node env) degrades to the
 * supplied fallback instead of throwing. Keys are namespaced so they never
 * collide with anything else the origin stores.
 */
const NS = 'karwan.shell.';

/** The Storage the prefs live in, or undefined when unavailable. Read through a
 *  getter so tests can install a fake on `globalThis` and so a throwing accessor
 *  (some privacy modes) is treated as "no storage". */
function store(): Storage | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}

/** Read a boolean pref, returning `fallback` when unset or unavailable. */
export function loadBool(key: string, fallback: boolean): boolean {
  try {
    const raw = store()?.getItem(NS + key);
    if (raw === null || raw === undefined) return fallback;
    return raw === '1';
  } catch {
    return fallback;
  }
}

/** Read a string pref, returning `undefined` when unset or unavailable. */
export function loadString(key: string): string | undefined {
  try {
    return store()?.getItem(NS + key) ?? undefined;
  } catch {
    return undefined;
  }
}

/** Persist a string pref, silently ignoring any storage failure. */
export function saveString(key: string, value: string): void {
  try {
    store()?.setItem(NS + key, value);
  } catch {
    /* storage unavailable or full — a remembered page is only a convenience */
  }
}

/** Forget a pref, silently ignoring any storage failure. */
export function removePref(key: string): void {
  try {
    store()?.removeItem(NS + key);
  } catch {
    /* storage unavailable — nothing remembered to forget */
  }
}

/** Persist a boolean pref, silently ignoring any storage failure. */
export function saveBool(key: string, value: boolean): void {
  try {
    store()?.setItem(NS + key, value ? '1' : '0');
  } catch {
    /* storage unavailable or full — a collapsed rail is not worth surfacing */
  }
}
